/**
 * nova-image-carousel.js
 * FILE: assets/nova-image-carousel.js
 * UPDATED: Added dot/bullet sync with active slide.
 */

class NovaImageCarousel {
  constructor(root) {
    this.root       = root;
    this.track      = root.querySelector('.nova-ic__track');
    this.slides     = [...root.querySelectorAll('.nova-ic__slide')];
    this.prev       = root.querySelector('.nova-ic__arrow--prev');
    this.next       = root.querySelector('.nova-ic__arrow--next');
    this.dots       = [...root.querySelectorAll('.nova-ic__dot')];
    this.total      = this.slides.length;
    this.current    = 0;
    this.startX     = 0;
    this.isDragging = false;

    this.visibleDesktop = parseInt(root.dataset.visible, 10) || 4;
    this.visibleMobile  = parseInt(root.dataset.visibleMobile, 10) || 1;
    this.gap            = parseInt(root.dataset.gap, 10) || 12;

    this._setup();
    this._bindEvents();
    window.addEventListener('resize', () => this._setup());
  }

  get visible() {
    return window.innerWidth < 750 ? this.visibleMobile : this.visibleDesktop;
  }

  _setup() {
    const visible    = this.visible;
    const gap        = this.gap;
    const viewport   = this.root.querySelector('.nova-ic__viewport');
    const vpWidth    = viewport.offsetWidth;
    const slideWidth = (vpWidth - (gap * (visible - 1))) / visible;

    this.slides.forEach(slide => {
      slide.style.width       = `${slideWidth}px`;
      slide.style.marginRight = `${gap}px`;
    });

    this.stepWidth = slideWidth + gap;
    this.current   = Math.min(this.current, Math.max(0, this.total - visible));
    this._moveTo(this.current, false);
  }

  _moveTo(index, animate = true) {
    const maxIndex  = Math.max(0, this.total - this.visible);
    this.current    = Math.max(0, Math.min(index, maxIndex));

    this.track.style.transition = animate
      ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
      : 'none';
    this.track.style.transform = `translateX(-${this.current * this.stepWidth}px)`;

    this._updateArrows();
    this._updateDots();
  }

  _updateArrows() {
    if (this.prev) this.prev.disabled = this.current <= 0;
    if (this.next) this.next.disabled = this.current >= this.total - this.visible;
  }

  _updateDots() {
    /* Highlight the dot matching the current slide index.
       If there are more slides than dots (unlikely but safe), clamp. */
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.current);
    });
  }

  _bindEvents() {
    this.prev?.addEventListener('click', () => this._moveTo(this.current - 1));
    this.next?.addEventListener('click', () => this._moveTo(this.current + 1));

    /* Dot clicks */
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this._moveTo(i));
    });

    /* Touch swipe */
    this.track.addEventListener('touchstart', (e) => {
      this.startX     = e.touches[0].clientX;
      this.isDragging = true;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      if (!this.isDragging) return;
      const diff = this.startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) this._moveTo(this.current + (diff > 0 ? 1 : -1));
      this.isDragging = false;
    }, { passive: true });

    /* Mouse drag */
    this.track.addEventListener('mousedown', (e) => {
      this.startX     = e.clientX;
      this.isDragging = true;
      this.track.style.cursor = 'grabbing';
    });

    window.addEventListener('mouseup', (e) => {
      if (!this.isDragging) return;
      const diff = this.startX - e.clientX;
      if (Math.abs(diff) > 40) this._moveTo(this.current + (diff > 0 ? 1 : -1));
      this.isDragging = false;
      this.track.style.cursor = '';
    });

    /* Keyboard */
    this.root.setAttribute('tabindex', '0');
    this.root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  this._moveTo(this.current - 1);
      if (e.key === 'ArrowRight') this._moveTo(this.current + 1);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[id^="nova-ic-"]').forEach(el => {
    new NovaImageCarousel(el);
  });
});